import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  FileText,
  Briefcase,
  GraduationCap,
  TrendingUp,
  Crown,
  ArrowRight,
  BarChart3,
  Clock,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getDocumentDisplayName } from '../../utils/documentDisplayName';
import { getUsageDisplayValues } from '../../utils/planLabels';

const QUICK_ACTIONS = [
  {
    type: 'contract',
    title: 'Analyze Contract',
    subtitle: 'Spot risks and key clauses fast',
    color: 'from-red-500 to-orange-500',
    icon: FileText,
  },
  {
    type: 'cv',
    title: 'Optimize CV',
    subtitle: 'Improve clarity and job fit',
    color: 'from-[var(--color-gradient-from)] to-[var(--color-gradient-to)]',
    icon: Briefcase,
  },
  {
    type: 'sop',
    title: 'Enhance SOP',
    subtitle: 'Strengthen your statement of purpose',
    color: 'from-green-500 to-teal-500',
    icon: GraduationCap,
  },
];

const CAROUSEL_INTERVAL_MS = 4000;

function QuickActionCarousel({ onSelect }) {
  const scrollRef = useRef(null);
  const [index, setIndex] = useState(0);
  const indexRef = useRef(0);
  const pausedRef = useRef(false);
  const resumeTimerRef = useRef(null);

  const goTo = useCallback((next, behavior = 'smooth') => {
    const el = scrollRef.current;
    if (!el || el.clientWidth === 0) return;

    const clamped = ((next % QUICK_ACTIONS.length) + QUICK_ACTIONS.length) % QUICK_ACTIONS.length;
    indexRef.current = clamped;
    setIndex(clamped);
    el.scrollTo({ left: el.clientWidth * clamped, behavior });
  }, []);

  const pauseAutoplay = useCallback(() => {
    pausedRef.current = true;
    if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    resumeTimerRef.current = setTimeout(() => {
      pausedRef.current = false;
    }, CAROUSEL_INTERVAL_MS * 2);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      if (pausedRef.current) return;
      goTo(indexRef.current + 1);
    }, CAROUSEL_INTERVAL_MS);

    return () => {
      clearInterval(timer);
      if (resumeTimerRef.current) clearTimeout(resumeTimerRef.current);
    };
  }, [goTo]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el || el.clientWidth === 0) return;

    const next = Math.round(el.scrollLeft / el.clientWidth);
    if (next >= 0 && next < QUICK_ACTIONS.length && next !== indexRef.current) {
      indexRef.current = next;
      setIndex(next);
    }
  };

  return (
    <div className="mb-2">
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        onTouchStart={pauseAutoplay}
        onMouseDown={pauseAutoplay}
        className="flex overflow-x-auto snap-x snap-mandatory scroll-smooth [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
      >
        {QUICK_ACTIONS.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.type}
              type="button"
              onClick={() => onSelect(item.type)}
              className="min-w-full snap-center flex items-center gap-3 elevated-card rounded-xl px-4 py-[18px] min-h-[88px] hover:border-[var(--color-primary)] transition-all text-left"
            >
              <div className={`w-11 h-11 rounded-xl bg-gradient-to-r ${item.color} flex items-center justify-center text-white shrink-0`}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[var(--color-text)] font-bold text-base">{item.title}</p>
                <p className="text-[var(--color-text-muted)] text-xs leading-4 mt-0.5">{item.subtitle}</p>
              </div>
              <ChevronRight className="w-[18px] h-[18px] text-[var(--color-text-muted)] shrink-0" />
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-center gap-4 mt-2.5">
        <button
          type="button"
          aria-label="Previous action"
          onClick={() => {
            pauseAutoplay();
            goTo(indexRef.current - 1);
          }}
          className="w-8 h-8 rounded-full bg-[var(--color-surface)] border border-[var(--color-border)] flex items-center justify-center text-[var(--color-text)]"
        >
          <ChevronLeft className="w-[18px] h-[18px]" />
        </button>

        <div className="flex items-center gap-1.5">
          {QUICK_ACTIONS.map((action, i) => (
            <button
              key={action.type}
              type="button"
              aria-label={`Go to ${action.title}`}
              onClick={() => {
                pauseAutoplay();
                goTo(i);
              }}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? 'w-[18px] bg-[var(--color-primary)]' : 'w-1.5 bg-[var(--color-border-subtle)]'
              }`}
            />
          ))}
        </div>

        <button
          type="button"
          aria-label="Next action"
          onClick={() => {
            pauseAutoplay();
            goTo(indexRef.current + 1);
          }}
          className="w-8 h-8 rounded-full bg-[var(--color-surface)] border border-[var(--color-border)] flex items-center justify-center text-[var(--color-text)]"
        >
          <ChevronRight className="w-[18px] h-[18px]" />
        </button>
      </div>
    </div>
  );
}

const MobileDashboard = ({ analytics, subscription, usage, user, loading, error, handleAnalyzeDocument, formatDate }) => {
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[var(--color-text-muted)]">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Helper to get current plan name (match desktop logic)
  const getCurrentPlanName = () => {
    if (usage?.subscription_plan) {
      return usage.subscription_plan.charAt(0).toUpperCase() + usage.subscription_plan.slice(1);
    }
    const plan = subscription?.subscription_plan || user?.subscription_plan;
    if (!plan) return 'Free';
    return plan.charAt(0).toUpperCase() + plan.slice(1);
  };

  const { used: usageUsed, limit: usageLimit } = getUsageDisplayValues(usage);

  return (
    <div className="min-h-screen bg-[var(--color-bg)] p-3 pb-6 flex flex-col gap-4">
      {/* Greeting */}
      <div className="mb-2 mt-2">
        <h1 className="text-2xl font-bold text-[var(--color-text)] mb-1">
          Hi, <span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--color-gradient-from)] to-[var(--color-gradient-to)]">{user?.full_name?.split(' ')[0]}</span> 👋
        </h1>
        <p className="text-sm text-[var(--color-text-muted)]">Welcome back! Ready to analyze and optimize your documents?</p>
      </div>

      {/* Quick Actions */}
      <QuickActionCarousel onSelect={handleAnalyzeDocument} />

      {/* Analytics Cards */}
      <div className="flex flex-col gap-3">
        {[
          {
            value: analytics.totalContracts + analytics.totalCVs + analytics.totalSOPs,
            label: 'Total Documents Analyzed',
            icon: BarChart3,
            color: 'from-[var(--color-gradient-from)] to-[var(--color-gradient-to)]',
            meta: <span className="ml-auto text-[var(--color-success)] text-xs flex items-center"><TrendingUp className="w-4 h-4 mr-1" />All Time</span>,
          },
          {
            value: analytics.totalRevamps,
            label: 'Total Documents Revamped',
            icon: FileText,
            color: 'from-[var(--color-gradient-from)] to-[var(--color-gradient-to)]',
            meta: <span className="ml-auto text-[var(--color-success)] text-xs flex items-center"><TrendingUp className="w-4 h-4 mr-1" />All Time</span>,
          },
          {
            value: analytics.totalGenerated,
            label: 'Total Documents Generated',
            icon: GraduationCap,
            color: 'from-green-500 to-teal-500',
            meta: <span className="ml-auto text-[var(--color-primary)] text-xs">All Time</span>,
          },
          {
            value: (analytics.totalAnalyzed ?? (analytics.totalContracts + analytics.totalCVs + analytics.totalSOPs)) + (analytics.totalRevamps ?? 0) + (analytics.totalGenerated ?? 0),
            label: 'Overall Documents Processed',
            icon: Briefcase,
            color: 'from-[var(--color-gradient-from)] to-[var(--color-accent)]',
            meta: <span className="ml-auto text-[var(--color-primary)] text-xs">All Time</span>,
          },
        ].map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.label} className="elevated-card rounded-xl p-3 flex items-center gap-3">
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-r ${stat.color} flex items-center justify-center`}>
                <Icon className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="text-lg font-bold text-[var(--color-text)]">{stat.value}</div>
                <div className="text-xs text-[var(--color-text-muted)]">{stat.label}</div>
              </div>
              {stat.meta}
            </div>
          );
        })}
      </div>

      {/* Subscription Status */}
      <div className="elevated-card rounded-2xl p-4 flex flex-col gap-2 mt-2">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-base font-semibold text-[var(--color-text)]">Subscription</h3>
          <Crown className="w-5 h-5 text-[var(--color-primary)]" />
        </div>
        <div className="flex justify-between text-xs text-[var(--color-text-muted)]">
          <span>Current Plan</span>
          <span className="text-[var(--color-primary)] font-semibold capitalize">{getCurrentPlanName()}</span>
        </div>
        <div className="flex justify-between text-xs text-[var(--color-text-muted)]">
          <span>Usage</span>
          <span className="text-[var(--color-text)]">{usageUsed}/{usageLimit}</span>
        </div>
        <div className="w-full bg-[var(--color-border)] rounded-full h-2 mb-2">
          <div 
            className="bg-gradient-to-r from-[var(--color-gradient-from)] to-[var(--color-gradient-to)] h-2 rounded-full transition-all duration-300"
            style={{ width: `${Math.min(((usageUsed / (usageLimit || 1)) * 100), 100)}%` }}
          />
        </div>
        {user?.subscription_plan !== 'enterprise' && (
          <button 
            onClick={() => navigate('/subscriptions')}
            className="brand-gradient-btn w-full px-4 py-2 rounded-lg flex items-center justify-center"
          >
            Upgrade Plan
            <ArrowRight className="w-4 h-4 ml-2" />
          </button>
        )}
      </div>

      {/* Recent Activity */}
      <div className="elevated-card rounded-2xl p-4 mt-2">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-[var(--color-text)]">Recent Analysis Activity</h3>
          <button 
            onClick={() => navigate('/recent-activities')}
            className="text-[var(--color-primary)] hover:text-[var(--color-primary-hover)] text-xs font-medium"
          >
            View All
          </button>
        </div>
        {analytics.recentDocuments.length > 0 ? (
          <div className="space-y-2">
            {analytics.recentDocuments.slice(0, 5).map((doc, index) => (
              <div key={index} className="flex items-center justify-between p-2 bg-[var(--color-surface-muted)] border border-[var(--color-border)] rounded-lg">
                <div className="flex items-center space-x-2">
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                    doc.document_type === 'contract' ? 'bg-red-500/20 text-red-400' :
                    doc.document_type === 'cv' ? 'bg-[var(--color-primary-soft)] text-[var(--color-primary)]' :
                    'bg-green-500/20 text-green-500'
                  }`}>
                    {doc.document_type === 'contract' ? <FileText className="w-4 h-4" /> :
                     doc.document_type === 'cv' ? <Briefcase className="w-4 h-4" /> :
                     <GraduationCap className="w-4 h-4" />}
                  </div>
                  <div>
                    <p className="text-[var(--color-text)] text-xs font-medium truncate max-w-32" title={getDocumentDisplayName(doc)}>
                      {getDocumentDisplayName(doc)}
                    </p>
                    <p className="text-[var(--color-text-muted)] text-[11px] capitalize">{doc.document_type} • {formatDate(doc.created_at)}</p>
                  </div>
                </div>
                <span className={`text-[10px] px-2 py-1 rounded-full ${
                  doc.status === 'completed' ? 'bg-green-500/15 text-[var(--color-success)]' :
                  doc.status === 'processing' ? 'bg-amber-500/15 text-[var(--color-warning)]' :
                  'bg-red-500/15 text-[var(--color-danger)]'
                }`}>
                  {doc.status}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6">
            <Clock className="w-10 h-10 text-[var(--color-text-muted)] mx-auto mb-3" />
            <p className="text-[var(--color-text-muted)] mb-2 text-xs">No recent activity</p>
            <button 
              onClick={() => handleAnalyzeDocument('contract')}
              className="brand-gradient-btn px-4 py-2 rounded-lg text-xs"
            >
              Analyze Your First Document
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileDashboard;
